<template>
	<div id="pricing-page" data-aos="fade">
		<!-- Hero -->
		<section class="top-section">
			<div class="container">
				<div class="row">
					<div class="col-12 col-lg-10 col-xl-8 mx-auto">
						<p class="hero-eyebrow" data-aos="fade-up">Planes</p>
						<h1 class="hero-title" data-aos="fade-up" data-aos-delay="100">Planes que se adaptan a tus necesidades</h1>
						<p class="hero-lead" data-aos="fade-up" data-aos-delay="200">
							Elige el plan perfecto para acceder a consultas legales, expedientes digitales y nuestro directorio de abogados expertos.
						</p>
					</div>
				</div>
			</div>
		</section>

		<!-- Pricing Cards -->
		<section class="plans-section">
			<div class="container">
				<div class="row">
					<div
						v-for="plan in plans"
						:key="plan.id"
						class="col-md-4 mb-4 mb-md-0"
						data-aos="fade-up" :data-aos-delay="plan.aosDelay"
					>
						<div class="pricing-card h-100 d-flex flex-column">
							<span v-if="plan.badge" class="pricing-badge">{{ plan.badge }}</span>

							<div class="pricing-card-header">
								<p class="plan-period">{{ plan.period }}</p>
								<h3 class="plan-name">{{ plan.name }}</h3>
								<p class="plan-desc">{{ plan.description }}</p>
								<div class="plan-price">
									<span class="plan-amount">{{ plan.price }}</span>
									<span class="plan-interval">{{ plan.interval }}</span>
								</div>
								<p v-if="plan.savings" class="plan-savings">
									{{ plan.savings }}
								</p>
							</div>

							<ul class="plan-features flex-grow-1">
								<li v-for="(feature, index) in plan.features" :key="index">
									<i class="fa-regular fa-check"></i>
									{{ feature }}
								</li>
							</ul>

							<b-button
								:to="plan.ctaTo"
								:variant="dark"
								size="lg"
								block
								class="mt-4 pricing-cta pricing-cta--navy">
								Comenzar
							</b-button>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- FAQ -->
		<section class="faq-section">
			<div class="container">
				<div class="row mb-4 mb-md-5">
					<div class="col-12 col-lg-10 col-xl-8 mx-auto">
						<p class="hero-eyebrow" data-aos="fade-up">FAQ</p>
						<h2 class="section-title" data-aos="fade-up" data-aos-delay="50">Preguntas frecuentes</h2>
					</div>
				</div>

				<div class="row">
					<div
						v-for="(faq, index) in faqs"
						:key="index"
						data-aos="fade-up" :data-aos-delay="faq.aosDelay"
						class="col-md-6 mb-3"
					>
						<div class="faq-card">
							<h3 class="faq-question">{{ faq.question }}</h3>
							<p class="faq-answer">{{ faq.answer }}</p>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- CTA -->
		<section class="cta-section">
			<div class="container">
				<div class="row" data-aos="fade-up">
					<div class="col-12 col-lg-8 mx-auto text-center">
						<h2 class="cta-title">¿Listo para comenzar?</h2>
						<p class="cta-lead">
							Únete a miles de usuarios que ya confían en unlawk para resolver sus problemas legales.
						</p>
						<div class="cta-actions d-flex flex-column flex-sm-row justify-content-center">
							<b-button to="/chat" variant="light" size="lg" class="cta-btn cta-btn--solid mb-2 mb-sm-0 mr-sm-3">
								Comenzar prueba gratuita
							</b-button>
							<b-button to="/" variant="outline-light" size="lg" class="cta-btn cta-btn--outline">
								Volver al inicio
							</b-button>
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
export default {
	data() {
		return {
			plans: [
				{
					id: 'mensual',
					aosDelay: 0,
					period: 'Mensual',
					name: 'Plan Mensual',
					description: 'Perfecto para consultas ocasionales',
					price: '$299',
					interval: '/mes',
					savings: null,
					badge: null,
					features: [
						'5 consultas con Lexi IA',
						'2 expedientes digitales',
						'Acceso al directorio de abogados',
						'Soporte por email',
					],
					ctaTo: { path: '/precios/checkout', query: { plan: 'mensual', precio: '299' } },
				},
				{
					id: 'semestral',
					aosDelay: 100,
					period: 'Semestral',
					name: 'Plan Semestral',
					description: 'Ideal para uso regular con descuento',
					price: '$1,499',
					interval: '/6 meses',
					savings: 'Ahorra $295 vs plan mensual',
					badge: 'Más popular',
					features: [
						'15 consultas con Lexi IA',
						'8 expedientes digitales',
						'Acceso completo al directorio',
						'Soporte prioritario',
						'Consultas telefónicas incluidas',
					],
					ctaTo: { path: '/precios/checkout', query: { plan: 'semestral', precio: '1499' } },
				},
				{
					id: 'anual',
					aosDelay: 200,
					period: 'Anual',
					name: 'Plan Anual',
					description: 'Máximo ahorro para uso intensivo',
					price: '$2,399',
					interval: '/año',
					savings: 'Ahorra $1,189 vs plan mensual',
					badge: null,
					features: [
						'Consultas ilimitadas con Lexi IA',
						'Expedientes digitales ilimitados',
						'Acceso VIP al directorio',
						'Soporte 24/7',
						'Asesor legal personal asignado',
						'Descuentos en servicios premium',
					],
					ctaTo: { path: '/precios/checkout', query: { plan: 'anual', precio: '2399' } },
				},
			],
			faqs: [
				{
					aosDelay: 0,
					question: '¿Puedo cambiar de plan en cualquier momento?',
					answer: 'Sí, puedes actualizar o cambiar tu plan en cualquier momento desde tu panel de usuario.',
				},
				{
					aosDelay: 50,
					question: '¿Qué incluye el soporte prioritario?',
					answer: 'Respuesta garantizada en menos de 2 horas y acceso directo a nuestro equipo de especialistas.',
				},
				{
					aosDelay: 100,
					question: '¿Hay período de prueba gratuito?',
					answer: 'Ofrecemos 7 días de prueba gratuita para que puedas explorar todas las funcionalidades.',
				},
				{
					aosDelay: 150,
					question: '¿Los precios incluyen IVA?',
					answer: 'Los precios mostrados no incluyen IVA, que se calculará según tu ubicación al momento del pago.',
				},
			],
		}
	},
}
</script>
